<template>
  <v-row class="pa-6 media-container">
    <v-col
      v-for="(item, i) in imgs"
      :key="i"
      class="d-flex child-flex"
      cols="3"
    >
      <div class="media-thumb">
        <v-img
          :src="item.url"
          :lazy-src="item.url"
          cover
          height="125"
          class="bg-grey-lighten-2 media-image"
        >
          <template #placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular
                indeterminate
                color="grey-lighten-5"
              ></v-progress-circular>
            </v-row>
          </template>
        </v-img>

        <div class="overlay">
          <v-btn
            color="transparent"
            icon
            variant="text"
            class="mr-2"
            @click="copyToClipboard(item.url)"
          >
            <v-icon>link</v-icon>
          </v-btn>
          <v-btn
            v-if="!isHelpDesk"
            color="transparent"
            icon
            variant="text"
            @click="removeImageDialogInit(item)"
          >
            <v-icon>delete_icon</v-icon>
          </v-btn>
        </div>
      </div>
    </v-col>

    <v-dialog v-model="removeImageDialog" persistent max-width="500px">
      <v-card :class="[isDarkTheme ? 'app-dark' : 'app-light']">
        <v-card-title class="text-h6 pb-0 justify-center text-center"
          >Image Removal</v-card-title
        >
        <v-card-text class="pb-2 text-center"
          >Are you sure you want to remove <br /><strong>{{
            imageToDelete.id
          }}</strong></v-card-text
        >
        <v-img
          :src="`${imageToDelete.url}`"
          width="50%"
          cover
          class="mx-auto mb-4"
        ></v-img>
        <v-col cols="12" sm="8" offset-sm="2">
          <v-text-field
            label="type DELETE below to confirm"
            placeholder="Type DELETE"
            v-model="removeConfirmation"
            variant="outlined"
          ></v-text-field>
        </v-col>
        <v-card-actions class="justify-center pb-4">
          <v-btn
            class="font-weight-bold text-white app-btn"
            color="#a5d867"
            variant="flat"
            :disabled="removeConfirmation != 'DELETE'"
            @click="removeImage(imageToDelete)"
            >Yes Delete it!</v-btn
          >
          <v-btn
            color="#592892"
            variant="flat"
            @click="removeImageDialog = false"
            >Nevermind</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Copy Image URL Modal -->
    <v-dialog v-model="imageCopyDialog" persistent max-width="500px">
      <v-card class="pa-4">
        <v-card-title class="text-h6 justify-center text-center"
          >You just copied</v-card-title
        >
        <v-img height="100" cover :src="imageCopyUrl"></v-img>
        <v-card-text class="text-subtitle-2 text-center"
          >{{ imageCopyUrl }}.</v-card-text
        >
        <v-card-actions class="justify-center pb-4">
          <v-btn
            class="font-weight-bold px-8 text-white"
            color="#a5d867"
            variant="flat"
            @click="imageCopyDialog = false"
            >OK</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
  import firebase from 'firebase'
  import { currentTheme } from '@/plugins/vuetify'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'Media',
    props: ['imgs'],
    data() {
      return {
        message: '',
        transparent: 'rgba(255, 255, 255, 0)',
        removeImageDialog: false,
        imageCopyDialog: false,
        imageCopyUrl: '',
        imageToDelete: '',
        removeConfirmation: '',
      }
    },
    computed: {
      isDarkTheme() {
        return currentTheme.value === 'dark'
      },
      isHelpDesk() {
        const roles = this.$store?.getters?.['UserData/getRoles'] || []
        return roles.includes('helpDesk')
      },
    },
    methods: {
      copyToClipboard(text) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            this.imageCopyDialog = true
            this.imageCopyUrl = text
          })
          .catch((err) => {
            alert('Failed to copy: ' + err)
          })
      },
      onError: function (e) {
        alert('Failed to copy texts ', e)
      },
      removeImageDialogInit(item) {
        if (this.isHelpDesk) {
          const key = 'media.deleteNotAllowed'
          const message =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot delete media.'
          alert(message)
          return
        }
        this.removeImageDialog = true
        this.removeConfirmation = ''
        this.imageToDelete = item
      },
      async removeImage(item) {
        if (this.isHelpDesk) {
          const key = 'media.deleteNotAllowed'
          const message =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot delete media.'
          alert(message)
          return
        }
        if (!item) {
          alert('Error: No image selected for delete.')
          return
        }

        // Already confirmed in dialog, just check the text field
        if (!matchesConfirmWord(this.removeConfirmation, 'DELETE')) {
          alert('Type DELETE to confirm image removal.')
          return
        }

        try {
          const storage = firebase.storage()
          const imageRef = storage.ref().child('/library/' + item.id)
          await imageRef.delete()

          this.$emit(
            'update-imgs',
            this.imgs.filter((img) => img.id !== item.id)
          )
          this.removeImageDialog = false
          this.removeConfirmation = ''
        } catch (error) {
          console.error(`file delete error occurred: ${error}`)
          alert('Failed to delete image from storage.')
        }
      },
    },
    created() {},
  }
</script>

<style>
  .media-container .v-card {
    transition: opacity 0.4s ease-in-out;
  }
  .media-container .v-image.on-hover .v-image__image {
    filter: blur(0px) grayscale(80%);
    opacity: 4;
  }
  .media-container .action-btns {
    display: flex;
    height: 100%;
    justify-content: center;
    align-items: center;
    background: radial-gradient(
      circle,
      rgba(0, 0, 0, 0.542454481792717) 0%,
      rgba(0, 0, 0, 0.9037990196078431) 100%
    );
    transition: opacity 0.4s ease-in-out;
  }
  .media-container .action-btns:not(.on-hover) {
    opacity: 0;
  }
  .media-container .show-btns {
    opacity: 1 !important;
  }
  .media-container svg path.a {
    fill: #fff !important;
  }
  .media-thumb {
    position: relative;
    width: 100%;
  }
  .media-thumb .overlay {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: radial-gradient(
      circle,
      rgba(0, 0, 0, 0.5) 0%,
      rgba(0, 0, 0, 0.9) 100%
    );
    transition: opacity 0.3s ease;
    z-index: 2;
  }
  .media-thumb:hover .overlay {
    opacity: 1;
  }
  .media-thumb .v-btn.v-btn--icon {
    color: white !important;
  }
</style>
